import { Button, Card, EmptyState } from "@heroui/react";
import { useState } from "react";

import { useTranslations } from "@/src/i18n/i18n";
import { ReportCard } from "@/src/pages/Reports/components/ReportCard";
import { ReportsFilter } from "@/src/pages/Reports/components/ReportsFilter";
import { ReportsHelp } from "@/src/pages/Reports/components/ReportsHelp";
import { ReportsSkeleton } from "@/src/pages/Reports/components/ReportsSkeleton";
import { useReportCounts } from "@/src/pages/Reports/hooks/useReportCounts";
import { useReports } from "@/src/pages/Reports/hooks/useReports";
import { useResolveReport } from "@/src/pages/Reports/hooks/useResolveReport";
import { useSanction } from "@/src/pages/Reports/hooks/useSanction";
import type { ReportStatus } from "@/src/pages/Reports/reports.type";

/**
 * File de modération (`/reports`).
 *
 * ⚠️ Pas de titre ici : `AppHeader` le déduit de `NAVIGATION`. Un écran ne
 * dessine que son contenu (cf. `Dashboard`).
 *
 * Le filtre vit dans un `useState` et non dans l'URL. C'est un choix, pas un
 * oubli : le jour où l'on voudra partager « la file des rejetés » par lien, le
 * remplacement par `useSearchParams` ne touche que cette ligne — mais tant que
 * personne ne le demande, un état local évite d'avoir à valider une chaîne
 * venue de la barre d'adresse.
 */
export function Reports() {
  const [status, setStatus] = useState<ReportStatus>("pending");
  const { reports, isLoading, isError, refetch } = useReports(status);
  const counts = useReportCounts();
  const { resolve, pendingId } = useResolveReport();
  const { sanction, sanctioningId, sanctioned } = useSanction();
  const t = useTranslations("reports");

  return (
    <div className="mx-auto flex w-full max-w-3xl flex-col gap-5">
      {/*
        Le volume de chaque file est porté par sa bascule : le compteur y dit à
        la fois où on est et ce qu'il y a ailleurs, là où un total isolé ne
        parlait que de la file affichée.

        ⚠️ Le défilement horizontal n'est pas décoratif : « Classés sans suite »
        plus son compteur touche déjà le bord à 420 px, et le français est le
        plus long des deux. Sans lui, la barre déborderait la page entière au
        lieu de défiler dans son coin.
      */}
      <div className="-mx-1 overflow-x-auto px-1 pb-1">
        <ReportsFilter counts={counts} onChange={setStatus} status={status} />
      </div>

      <ReportsHelp />

      {isLoading ? <ReportsSkeleton /> : null}

      {/*
        L'échec a son propre écran **en plus** du toast : le toast dit pourquoi
        et disparaît, la page doit encore offrir un moyen de réessayer. Sans
        cette branche, une erreur ressemblerait à une file vide — le contraire
        de ce qu'il faut comprendre.
      */}
      {isError ? (
        <ReportsNotice
          description={t("error-description")}
          title={t("error-title")}
          onRetry={() => refetch()}
        />
      ) : null}

      {!isLoading && !isError && reports.length === 0 ? (
        <ReportsNotice
          description={
            status === "pending" ? t("empty-pending") : t("empty-closed")
          }
          title={t("empty-title")}
        />
      ) : null}

      {reports.length > 0 ? (
        <ul className="flex flex-col gap-3">
          {reports.map((report) => (
            <li key={report.id}>
              <ReportCard
                isResolving={pendingId === report.id}
                isSanctioned={sanctioned.has(report.id)}
                isSanctioning={sanctioningId === report.id}
                onResolve={resolve}
                onSanction={sanction}
                report={report}
              />
            </li>
          ))}
        </ul>
      ) : null}
    </div>
  );
}

type ReportsNoticeProps = {
  title: string;
  description: string;
  /** Absent pour une file vide : il n'y a rien à réessayer. */
  onRetry?: () => void;
};

/**
 * File vide ou file en erreur — même forme, deux messages.
 *
 * Local au fichier, comme `NavLink` l'est à `SidebarNav` : tant qu'un seul
 * écran s'en sert, le sortir dans `components/` ajouterait un fichier sans
 * ajouter de réutilisation.
 */
function ReportsNotice({ title, description, onRetry }: ReportsNoticeProps) {
  const t = useTranslations("reports");

  return (
    <Card className="p-10">
      <Card.Content>
        <EmptyState className="flex flex-col items-center gap-2 text-center">
          <p className="text-base font-medium">{title}</p>
          <p className="max-w-md text-pretty text-muted">{description}</p>

          {onRetry ? (
            <div className="pt-2">
              <Button size="sm" variant="secondary" onPress={onRetry}>
                {t("retry")}
              </Button>
            </div>
          ) : null}
        </EmptyState>
      </Card.Content>
    </Card>
  );
}
